// src/array/array.ts
import { VyElementNotFoundError } from "@vy/error";
import { isObject as isObject2 } from "lodash";

// src/function/function.ts
import _ from "lodash";
function isFunction(func) {
  return typeof func === "function";
}
function isAccessor(accessor) {
  return isFunction(accessor) && accessor.length === 1;
}
function debounce(func, wait = 250) {
  return _.debounce(func, wait);
}

// src/string/string-consts.ts
var EMPTY_STRING = "";
var CASE_INSENSITIVE_REGEX = "i";
var NEW_LINE = "\n";
var SPACE = " ";

// src/string/string.ts
function isString(value) {
  return typeof value === "string";
}
function isNotString(value) {
  return !isString(value);
}
function validateIsString(value, name) {
  if (!isString(value)) {
    raise(`${name ?? "value"} is not a string`);
  }
}
function isEmptyString(value) {
  return isNullOrUndefined(value) || value === EMPTY_STRING;
}
function isNotEmptyString(value) {
  return !isEmptyString(value);
}
function validateNotEmptyString(value, name) {
  if (isEmptyString(value)) {
    raise(`${name ?? "value"} is empty string`);
  }
}
function caseInsensitiveIncludes(value, search) {
  const regex = new RegExp(escapeRegex(search), CASE_INSENSITIVE_REGEX);
  return value.match(regex) !== null;
}
function sanitizeFilename(input) {
  const fileNameWithoutSpaces = input.replace(/\s+/g, "_");
  const validFileName = fileNameWithoutSpaces.replace(/[\/\.\\:*?"<>|]+/g, "");
  return validFileName === "" || validFileName.startsWith(".") ? "file" + validFileName : validFileName;
}
function desanitizeFilename(sanitized) {
  return sanitized.replace(/_/g, " ").replace(/\s+/g, " ").trim();
}
function substringFromEnd(value, length) {
  return value.substring(value.length - length);
}
function shortenString(str, charCount) {
  const digits = -Math.abs(charCount);
  return str.slice(digits);
}
function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function toLowerCase(value) {
  return value.toLowerCase();
}
function toUpperCase(value) {
  return value.toUpperCase();
}
function jsonStringToList(jsonString) {
  return JSON.parse(jsonString);
}

// src/error/error.ts
function raise(messageOrError) {
  if (isString(messageOrError)) {
    throw new Error(messageOrError);
  }
  throw messageOrError;
}
function isError(error) {
  return error instanceof Error;
}
function validateError(error) {
  if (!isError(error)) {
    raise("Error is not defined");
  }
}
function raiseUnreachable(unreachableType) {
  return raise(`Unreachable type: ${unreachableType}`);
}
function raiseUnsupported(unsupportedValue) {
  return raise(`Unsupported value: ${unsupportedValue}`);
}
async function runSafely(promiseOrFunction) {
  try {
    const result = isFunction(promiseOrFunction) ? await promiseOrFunction() : await promiseOrFunction;
    return [void 0, result];
  } catch (error) {
    return [error, void 0];
  }
}
function caught(operation, ErrorClass) {
  try {
    const result = operation();
    return { result };
  } catch (error) {
    return error instanceof ErrorClass ? { error } : raise(error);
  }
}
async function caughtAsync(asyncOperation, ErrorClass) {
  try {
    const result = await asyncOperation;
    return { result };
  } catch (error) {
    return error instanceof ErrorClass ? { error } : raise(error);
  }
}

// src/vy-error/vy-error.ts
var VyError = class extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
    Object.setPrototypeOf(this, new.target.prototype);
  }
};

// src/vy-error/vy-errors/api-error/api-error.ts
import { getReasonPhrase } from "http-status-codes";
var ApiError = class extends VyError {
  constructor(message, statusCode) {
    const reasonPhrase = getReasonPhrase(statusCode);
    super(message, reasonPhrase);
    this.statusCode = statusCode;
  }
};

// src/vy-error/vy-errors/api-error/api-errors/api-validation-error/api-validation-errors/path-param-error.ts
import { VyBadRequestError } from "@vy/error";

// src/number/index.ts
var MAXIMUM_FRACTION_DIGITS = 2;
function formatNumber(num, maximumFractionDigits = MAXIMUM_FRACTION_DIGITS, options, formatMillions = false) {
  const numValue = num ?? 0;
  if (formatMillions && numValue >= 1e6) {
    const millions = Math.round(numValue / 1e6);
    return `${millions}M`;
  }
  const minimumFractionDigits = getMinimumFractionDigits(numValue);
  const cappedMinimumFractionDigits = Math.min(minimumFractionDigits, maximumFractionDigits);
  return numValue.toLocaleString("en-US", {
    style: "decimal",
    minimumFractionDigits: cappedMinimumFractionDigits,
    maximumFractionDigits,
    ...options
  });
}
function getMinimumFractionDigits(number) {
  if (Number.isInteger(number)) {
    return 0;
  }
  const fractionalPart = number.toString().split(".")[1];
  const startingZeroesCount = Array.from(fractionalPart).findIndex((digit) => digit !== "0");
  return Math.max(startingZeroesCount + 1, 2);
}
function parseNumber(numStr) {
  const parsedNumber = Number(numStr);
  return isNaN(parsedNumber) ? void 0 : parsedNumber;
}
function parseNumberOrDefault(numStr, defaultValue) {
  return parseNumber(numStr) ?? defaultValue;
}
function isNumber(value) {
  return typeof value === "number";
}
function isNumberValid(value) {
  return isNotNullOrUndefined(value) && !isNaN(value);
}
function isNotNumber(value) {
  return !isNumber(value);
}
function validateNumber(value) {
  if (isNotNumber(value)) {
    throw new Error("Value is not a number");
  }
}
function sumBy(items, keyOrAccessor) {
  if (isNullOrUndefined(items)) {
    return 0;
  }
  const getNumericalValue = (item, index) => isFunction(keyOrAccessor) ? isAccessor(keyOrAccessor) ? keyOrAccessor(item) : keyOrAccessor(item, index) : item[keyOrAccessor];
  return items.reduce((total, item, itemIndex) => getNumericalValue(item, itemIndex) + total, 0);
}
function avgBy(items, keyOrAccessor) {
  if (isEmptyArray(items)) {
    return 0;
  }
  const getNumericalValue = (item, index) => isFunction(keyOrAccessor) ? isAccessor(keyOrAccessor) ? keyOrAccessor(item) : keyOrAccessor(item, index) : item[keyOrAccessor];
  const itemsSum = sumBy(items, getNumericalValue);
  return itemsSum / items.length;
}
function isBetweenExclusive(value, min2, max2) {
  return value > min2 && value < max2;
}
function isBetweenInclusive(value, min2, max2) {
  return value >= min2 && value <= max2;
}
function isNotBetweenInclusive(value, min2, max2) {
  return !isBetweenInclusive(value, min2, max2);
}
function sum(...numbers) {
  return numbers.reduce((total, num) => total + (num ?? 0), 0);
}
function isBiggerThan(num, threshold) {
  return num > threshold;
}
function isInTheMillions(num) {
  return isBiggerThan(num, 10 ** 6);
}
function safeDivision(numerator, denominator) {
  return denominator === 0 ? 0 : numerator / denominator;
}
function calculatePercentageFromNumber(percentageAmount, amount) {
  return percentageAmount / 100 * amount;
}
function percentage(numerator, denominator) {
  return safeDivision(numerator, denominator) * 100;
}
function isPositive(value) {
  return isNotNullOrUndefined(value) && value > 0;
}
function isNegative(value) {
  return value < 0;
}
function isNonNegative(value) {
  return value >= 0;
}
function percentageRatio(numerator) {
  return numerator / 100;
}
function getValuesBetweenIncluding(min2, max2, step = 1) {
  const values2 = [];
  for (let i = min2; i <= max2; i += step) {
    values2.push(i);
  }
  return values2;
}
function toNegativeOptional(value) {
  return isNullOrUndefined(value) ? void 0 : -value;
}
function withNumberToPrecision(num, precision) {
  const numValue = num ?? 0;
  const stringifiedResult = numValue.toPrecision(precision);
  return Number(stringifiedResult);
}

// src/zod-error-formatter/zod-error-formatter-consts.ts
var VALIDATION_ERROR_CODES = Object.freeze({
  PARAMETER_MISSING: "parameter_missing",
  PARAMETER_INVALID: "parameter_invalid",
  VALIDATION_FAILED: "validation_failed"
});
var UNKNOWN_PARAM = "unknown";

// src/zod-error-formatter/zod-error-formatter.ts
function isErrorWithFormattedZodError(error) {
  return "formattedError" in error && error.formattedError != null;
}
function formatZodError(zodError, schemaKeys) {
  const flatIssues = zodError.issues.flatMap((issue, index) => flattenIssue(issue, index, schemaKeys));
  if (isSingleItem(flatIssues)) {
    const { param, message, isMissing } = flatIssues[0];
    const code = isMissing ? VALIDATION_ERROR_CODES.PARAMETER_MISSING : VALIDATION_ERROR_CODES.PARAMETER_INVALID;
    const summaryMessage = isMissing ? `Missing required parameter: ${param}` : `${param}: ${message}`;
    return { code, message: summaryMessage, param };
  }
  return {
    code: VALIDATION_ERROR_CODES.VALIDATION_FAILED,
    message: `${flatIssues.length} validation errors in request`,
    errors: flatIssues.map(({ param, message }) => ({ param, message }))
  };
}
function flattenIssue(issue, index, schemaKeys) {
  if (issue.code === "invalid_union") {
    return flattenUnionIssue(issue, index, schemaKeys);
  }
  const param = resolveParamName(issue.path, index, schemaKeys);
  const isMissing = issue.code === "invalid_type" && issue.received === "undefined";
  return [{ param, message: issue.message, isMissing }];
}
function flattenUnionIssue(issue, index, schemaKeys) {
  const param = resolveParamName(issue.path, index, schemaKeys);
  const allSubIssues = issue.unionErrors.flatMap((unionError) => unionError.issues);
  const allMissing = allSubIssues.every(
    (subIssue) => subIssue.code === "invalid_type" && subIssue.received === "undefined"
  );
  return [{ param, message: issue.message, isMissing: allMissing }];
}
function resolveParamName(path, index, schemaKeys) {
  if (isNotEmptyArray(path)) {
    return formatPath(path);
  }
  if (isNullOrUndefined(schemaKeys)) {
    return UNKNOWN_PARAM;
  }
  return schemaKeys[index] ?? UNKNOWN_PARAM;
}
function formatPath(path) {
  return path.reduce((result, segment) => {
    if (isNumber(segment)) {
      return `${result}[${segment}]`;
    }
    return result ? `${result}.${segment}` : segment;
  }, "");
}

// src/vy-error/vy-errors/api-error/api-errors/api-validation-error/api-validation-errors/path-param-error.ts
var PathParamError = class extends VyBadRequestError {
  formattedError;
  constructor(zodError, schemaKeys) {
    const formatted = formatZodError(zodError, schemaKeys);
    super(formatted.message, { validatorError: zodError.message });
    this.formattedError = formatted;
  }
};

// src/vy-error/vy-errors/api-error/api-errors/api-validation-error/api-validation-errors/query-param-error.ts
import { VyBadRequestError as VyBadRequestError2 } from "@vy/error";
var QueryParamError = class extends VyBadRequestError2 {
  formattedError;
  constructor(zodError, schemaKeys) {
    const formatted = formatZodError(zodError, schemaKeys);
    super(formatted.message, { validatorError: zodError.message });
    this.formattedError = formatted;
  }
};

// src/vy-error/vy-errors/api-error/api-errors/api-validation-error/api-validation-error.ts
import { StatusCodes } from "http-status-codes";
var ApiValidationError = class extends ApiError {
  constructor(zodError) {
    super(zodError.message, StatusCodes.BAD_REQUEST);
    this.zodError = zodError;
  }
};

// src/vy-error/vy-errors/api-error/api-errors/api-validation-error/api-validation-errors/request-payload-error.ts
var RequestPayloadError = class extends ApiValidationError {
  get message() {
    return `Invalid request payload: ${this.zodError.message}`;
  }
  constructor(zodError) {
    super(zodError);
  }
};

// src/vy-error/vy-errors/api-error/api-errors/resource-not-found-error.ts
import { StatusCodes as StatusCodes2 } from "http-status-codes";
var ResourceNotFoundError = class extends ApiError {
  constructor(entityName, id) {
    const message = `${entityName} with id ${id} not found`;
    super(message, StatusCodes2.NOT_FOUND);
  }
};
var ResourceNotFoundSparseError = class extends ApiError {
  constructor() {
    super("Entity not found", StatusCodes2.NOT_FOUND);
  }
};

// src/vy-error/vy-errors/api-error/api-errors/bad-credentials-error.ts
var BadCredentialsError = class extends VyError {
  constructor(originalError) {
    super(originalError.message);
  }
};

// src/vy-error/vy-errors/generic-error/generic-errors/empty-object-error.ts
import { VyError as VyError2 } from "@vy/error";
var VyEmptyObjectError = class extends VyError2 {
  constructor(objectName) {
    super(`${objectName ?? "Object"} is not defined`);
  }
};

// src/vy-error/vy-errors/generic-error/generic-errors/null-or-undefined-error.ts
import { VyError as VyError3 } from "@vy/error";
var VyNullOrUndefinedError = class extends VyError3 {
  constructor(objectName = "Value") {
    super(`${objectName} is null or undefined`);
    this.objectName = objectName;
  }
  isSameAsFailedObjectName(objectName) {
    return this.objectName === objectName;
  }
};

// src/vy-error/error-handlers/unknown-errors-handler.ts
function handleUnknownError(parameters) {
  const { rawError, errorClass, buildErrorToThrow } = parameters;
  if (rawError instanceof errorClass) {
    return buildErrorToThrow();
  }
  return rawError;
}

// src/object/object.ts
function isObject(obj) {
  return typeof obj === "object";
}
function isEmptyObject(object) {
  if (isNullOrUndefined(object)) {
    return true;
  }
  const objectKeys = keys(object);
  return isEmptyArray(objectKeys);
}
function isNotEmptyObject(object) {
  return !isEmptyObject(object);
}
function isDeepEmptyObject(obj) {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value === void 0) {
        continue;
      }
      if (typeof value === "object") {
        if (!isDeepEmptyObject(value)) {
          return false;
        }
      } else {
        return false;
      }
    }
  }
  return true;
}
function isKeyOf(object, key) {
  return key in object;
}
function keyExists(object, key) {
  return isNotNullOrUndefined(key) && isKeyOf(object, key);
}
function isNullOrUndefined(obj) {
  return obj === null || obj === void 0;
}
function isNotNullOrUndefined(obj) {
  return !isNullOrUndefined(obj);
}
function areSomeNullOrUndefined(...objects) {
  return objects.some(isNullOrUndefined);
}
function areAllNullOrUndefined(...objects) {
  return objects.every(isNullOrUndefined);
}
function extend(object, extension) {
  return {
    ...object,
    ...extension
  };
}
function keys(object) {
  return Object.keys(object);
}
function values(object) {
  return Object.values(object);
}
function extractValues(object, key) {
  const objectValues = values(object);
  return extract(objectValues, key);
}
function entries(object) {
  return Object.entries(object);
}
function omit(object, keyToOmit) {
  const { [keyToOmit]: _2, ...rest } = object;
  return rest;
}
function getObjectLength(object) {
  const objectKeys = keys(object);
  return objectKeys.length;
}
function getRequiredObjectProperty(object, key) {
  const value = object[key];
  return isNotNullOrUndefined(value) ? value : raise(`Property ${key.toString()} is not defined`);
}
function getOptionalObjectProperty(object, key) {
  const value = object[key];
  return value;
}
function getObjectPropertyOrDefault(object, key, defaultValue) {
  const value = object[key];
  return isNotNullOrUndefined(value) ? value : defaultValue;
}
function validateNotNullOrUndefined(value, name) {
  if (isNullOrUndefined(value)) {
    raise(new VyNullOrUndefinedError(name));
  }
}
function validateNotEmptyObject(object, name) {
  object ?? raise(new VyEmptyObjectError(name));
}
function validateEnum(value, enumObject) {
  if (!values(enumObject).some((enumValue) => enumValue === value)) {
    raise(`Value ${value} is not a valid enum value`);
  }
}
function validateEnumArray(items, enumObject) {
  items.forEach((value) => validateEnum(value, enumObject));
}
function getFlatObjectValues(object) {
  return isNotNullOrUndefined(object) ? values(object).flat() : [];
}
function getModifiedFields(newObj, oldObj) {
  return keys(newObj).reduce((acc, key) => {
    if (!isKeyOf(newObj, key)) {
      return acc;
    }
    const newValue = newObj[key];
    const oldValue = oldObj[key];
    if (isObject(newValue) && isObject(oldValue)) {
      const nestedUpdatedFields = getModifiedFields(newValue, oldValue);
      if (isNotEmptyObject(nestedUpdatedFields)) {
        acc[key] = nestedUpdatedFields;
      }
    } else if (newValue !== oldValue) {
      acc[key] = newValue;
    }
    return acc;
  }, {});
}
function removeUndefinedAndNullValues(obj) {
  const allEntries = Object.entries(obj);
  const definedEntries = allEntries.filter(([_key, value]) => isNotNullOrUndefined(value));
  const filteredObject = Object.fromEntries(definedEntries);
  return filteredObject;
}
function omitKey(obj, keyToOmit) {
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => k !== keyToOmit)
  );
}

// src/object/object-consts.ts
var EMPTY_OBJECT = {};

// src/object/object-types/strict-omit.ts
function conditionalWithDefault(value, defaultValue, condition) {
  return condition() ? withDefault(value, defaultValue) : defaultValue;
}
function withDefault(value, defaultValue) {
  if (isNumber(value) && isNaN(value)) {
    return defaultValue;
  }
  return value ?? defaultValue;
}

// src/serializer.ts
import { VySerializeError } from "@vy/error";
import safeStringify from "fast-safe-stringify";
function vySerialize(obj, replacer, space) {
  try {
    return safeStringify(obj, replacer ?? void 0, space);
  } catch (error) {
    if (error instanceof Error) {
      throw new VySerializeError("Fail to serialize object", error);
    }
    throw new VySerializeError("Fail to serialize object");
  }
}

// src/array/array-consts.ts
var EMPTY_ARRAY = [];
var EMPTY_BATCH_ITERATION_DATA = {
  currentBatchSize: 0,
  items: []
};

// src/array/array.ts
function isEmptyArray(array) {
  return !array?.length;
}
function isNotEmptyArray(array) {
  return !isEmptyArray(array);
}
function isArray(array) {
  return Array.isArray(array);
}
function isNotArray(array) {
  return !isArray(array);
}
function replaceInIndex(array, index, newItem) {
  return clonedArray(array).map((originalItem, i) => i !== index ? originalItem : newItem);
}
function extendInIndex(array, index, extension) {
  return clonedArray(array).map(
    (originalItem, i) => i !== index ? originalItem : extend(originalItem, extension)
  );
}
function clonedArray(array) {
  return [...array];
}
function removeInIndex(array, index) {
  return isNotEmptyArray(array) ? clonedArray(array).filter((_2, i) => i !== index) : [];
}
function purge(array) {
  return array.filter(isNotNullOrUndefined);
}
function purgeFlat(array) {
  const flattened = array.flat();
  return purge(flattened);
}
function isMultiple(array) {
  return isNotEmptyArray(array) && isLengthGreaterThan(array, 1);
}
function isSingleItem(array) {
  return isLengthOf(array, 1);
}
function isLengthOf(array, length) {
  return isNotEmptyArray(array) && array.length === length;
}
function isLengthGreaterThan(array, length) {
  return array.length > length;
}
function sortDescending(items, keyOrAccessor) {
  if (isAccessor(keyOrAccessor)) {
    return sort(items, keyOrAccessor).reverse();
  }
  return sort(items, keyOrAccessor).reverse();
}
function purgeMap(items, keyOrAccessor) {
  const extractedItems = extract(items, keyOrAccessor);
  return purge(extractedItems);
}
function extract(items, keyOrAccessor) {
  if (isEmptyArray(items)) {
    return [];
  }
  const accessor = isFunction(keyOrAccessor) ? keyOrAccessor : (item) => item[keyOrAccessor];
  return items.map((item) => accessor(item));
}
function uniqueExtract(items, keyOrAccessor) {
  const extractedItems = extract(items, keyOrAccessor);
  const isComplexObject = isArray(extractedItems[0]) || isObject2(extractedItems[0]);
  return isComplexObject ? uniqueObjects(extractedItems) : uniqueArray(extractedItems);
}
function flatExtract(items, keyOrAccessor) {
  const extractedItems = extract(items, keyOrAccessor);
  return extractedItems.flat();
}
function last(items) {
  validateIsArray(items, "items");
  return isEmptyArray(items) ? void 0 : items[items.length - 1];
}
function requiredLast(items) {
  const lastItem = last(items);
  validateNotNullOrUndefined(lastItem, "lastItem");
  return lastItem;
}
function first(items) {
  validateIsArray(items, "items");
  return isEmptyArray(items) ? void 0 : items[0];
}
function filter(items, value, accessorOrKey) {
  const accessor = isFunction(accessorOrKey) ? accessorOrKey : (item) => `${item[accessorOrKey]}`;
  return items.filter((item) => caseInsensitiveIncludes(accessor(item), value));
}
function sort(items, keyOrAccessor) {
  const accessor = isFunction(keyOrAccessor) ? keyOrAccessor : (item) => item[keyOrAccessor];
  return items.sort((a, b) => accessor(a) > accessor(b) ? 1 : -1);
}
function intersect(array1, array2) {
  return array1.filter((item) => array2.includes(item));
}
function outersect(array1, array2) {
  return array1.filter((item) => !array2.includes(item));
}
function outersectBy(array1, array2, accessorOrKey) {
  const accessor = isFunction(accessorOrKey) ? accessorOrKey : (item) => item[accessorOrKey];
  return array1.filter((item) => !array2.some((item2) => accessor(item) === accessor(item2)));
}
function makeMultiple(itemOrItems) {
  return Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
}
function uniqueBy(array, accessorOrKey) {
  const accessor = isFunction(accessorOrKey) ? accessorOrKey : (item) => item[accessorOrKey];
  return array.filter(
    (item, item1Index) => array.findIndex((item2) => accessor(item) === accessor(item2)) === item1Index
  );
}
function uniq(array) {
  const isPrimitive = typeof array !== "object" && typeof array !== "function" || array === null;
  return uniqueBy(array, (item) => isPrimitive ? item : vySerialize(item));
}
function requiredAt(array, index, arrayName) {
  const item = array.at(index);
  if (isNullOrUndefined(item)) {
    throw new VyElementNotFoundError(`No item found at index ${index} in ${arrayName ?? "array"}`);
  }
  return item;
}
function findRequired(array, predicate, arrayName) {
  const foundItem = array.find(predicate);
  if (!foundItem) {
    throw new VyElementNotFoundError(`No item found in ${arrayName ?? "array"}`);
  }
  return foundItem;
}
function findRequiredIndex(array, predicate, arrayName) {
  const foundIndex = array.findIndex(predicate);
  if (foundIndex === -1) {
    throw new VyElementNotFoundError(`No item found in ${arrayName ?? "array"}`);
  }
  return foundIndex;
}
function findRequiredIndexOf(array, item, arrayName) {
  return findRequiredIndex(array, (arrayItem) => arrayItem === item, arrayName);
}
function uniqueObjects(array) {
  const seenObjectsJson = /* @__PURE__ */ new Set();
  return array.filter((item) => {
    const json = vySerialize(item);
    const isNew = !seenObjectsJson.has(json);
    if (isNew) {
      seenObjectsJson.add(json);
    }
    return isNew;
  });
}
function newArrayOfLength(length) {
  return Array.from({ length });
}
function validateNotEmptyArray(array, arrayName) {
  if (isEmptyArray(array)) {
    throw new Error(`${arrayName ?? "array"} is empty`);
  }
}
function validateArray(array, arrayName) {
  if (!isArray(array)) {
    throw new Error(`${arrayName ?? "array"} is not an array`);
  }
}
function batch(items, batchSize) {
  return newArrayOfLength(Math.ceil(items.length / batchSize)).map(
    (_2, batchIndex) => items.slice(batchIndex * batchSize, (batchIndex + 1) * batchSize)
  );
}
function batchByMaxPayloadSize(items, maxPayloadSize, toPayload) {
  const sizeLimitedBatches = items.reduce(
    (batchIterationData, item) => {
      const payload = toPayload(item);
      const payloadSize = Buffer.byteLength(payload, "utf8");
      validateNotEmptyArray(batchIterationData, "batchIterationData");
      const currentBatch = last(batchIterationData);
      if (currentBatch.currentBatchSize + payloadSize > maxPayloadSize) {
        const newBatch = {
          currentBatchSize: payloadSize,
          items: [item]
        };
        return [...batchIterationData, newBatch];
      }
      const completedBatches = batchIterationData.slice(0, -1);
      const updatedCurrentBatch = {
        currentBatchSize: currentBatch.currentBatchSize + payloadSize,
        items: [...currentBatch.items, item]
      };
      return [...completedBatches, updatedCurrentBatch];
    },
    [EMPTY_BATCH_ITERATION_DATA]
  );
  return extract(sizeLimitedBatches, "items");
}
function filterBy(array, query, ...fields) {
  const filterByField = (item, field) => {
    const fieldValue = item[field];
    return isString(fieldValue) && caseInsensitiveIncludes(fieldValue, query);
  };
  return array.filter((item) => fields.some((field) => filterByField(item, field)));
}
function filterByExact(array, query, ...fields) {
  const filterByField = (item, field) => {
    const fieldValue = item[field];
    return isString(fieldValue) && fieldValue === query;
  };
  return array.filter((item) => fields.some((field) => filterByField(item, field)));
}
function isNotIn(array, ...items) {
  return isEmptyArray(array) || array.every((item) => !items.includes(item));
}
function every(array, predicate) {
  return isNotEmptyArray(array) && array.every(predicate);
}
function append(array, value) {
  return [...array, value];
}
function remove(array, value) {
  return array.filter((item) => item !== value);
}
function removeBy(array, predicate) {
  return array.filter((item) => !predicate(item));
}
function removeByField(array, value, field) {
  return removeBy(array, (item) => item[field] === value);
}
function takeFromStart(array, amount) {
  return array.slice(0, amount);
}
function takeFromEnd(array, amount) {
  return array.slice(-amount);
}
function take(array, startIndex, endIndex) {
  return array.slice(startIndex, endIndex);
}
function fillArrayOfLength(length, item) {
  return Array.from({ length }).map(() => item);
}
function validateIsArray(value, name) {
  if (isNotArray(value)) {
    throw new Error(`${name} is not an array`);
  }
}
function validateIsStringArray(value, name) {
  if (isNotArray(value)) {
    throw new Error(`${name} is not an array`);
  }
  return value.every((item) => isString(item));
}
function validateIsSingleItem(array, name) {
  if (!isSingleItem(array)) {
    const errorMessage = `${name} is not a single item. ${isNullOrUndefined(array) ? "it is null" : `it has ${array.length} items`}`;
    throw new Error(errorMessage);
  }
}
function validateNotMultipleItems(array, name) {
  if (isMultiple(array)) {
    const errorMessage = `${name} can not have multiple items. it has ${array.length} items`;
    throw new Error(errorMessage);
  }
}
function whenNotEmptyArray(operationOnArray, array) {
  return isEmptyArray(array) ? void 0 : operationOnArray(array);
}
function extractIds(items) {
  return extract(items, "id");
}
function uniqueArray(array) {
  return [...new Set(array)];
}
function asLastOrEmptyArray(array) {
  return isNotEmptyArray(array) ? [last(array)] : [];
}

// src/buffer.ts
function serializeToJsonBuffer(item, encoding) {
  return Buffer.from(vySerialize(item), encoding);
}
function uint8ArrayToBase64(bytes) {
  const binaryString = String.fromCharCode(...bytes);
  return btoa(binaryString);
}

// src/common.ts
function withNewOptionalValue(value, newValueOption, defaultNoValue) {
  if (isFunction(newValueOption)) {
    return isNotNullOrUndefined(value) ? newValueOption(value) : defaultNoValue;
  }
  return isNotNullOrUndefined(value) ? newValueOption : defaultNoValue;
}

// src/date/date.ts
import { isDayjs, unix as unix2 } from "dayjs";
import { first as first2, max, maxBy, min } from "lodash";

// src/boolean/index.ts
function isBoolean(value) {
  return typeof value === "boolean";
}

// src/date/vy-date/dayjs-extended.ts
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localizedFormat from "dayjs/plugin/localizedFormat";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(quarterOfYear);
var dayjs_extended_default = dayjs;

// src/date/vy-date/index.ts
import { duration as duration2 } from "dayjs";
import { unix } from "dayjs";
function vyDate(date, options = {}) {
  const { format, keepLocalTime, localeOrStrict, strict } = {
    keepLocalTime: false,
    localeOrStrict: false,
    strict: false,
    ...options
  };
  const locale = isString(localeOrStrict) ? localeOrStrict : void 0;
  const strictValue = isBoolean(localeOrStrict) ? localeOrStrict : strict;
  let time = dayjs_extended_default(date, format, locale, strictValue);
  if (!time.isUTC()) {
    time = time.utc(keepLocalTime);
  }
  return time;
}

// src/date/date.ts
function isDateBetween(date, startOrPeriod, endTime) {
  return isDate(startOrPeriod) ? date >= startOrPeriod && isNotNullOrUndefined(endTime) && date <= endTime : isDateBetween(date, startOrPeriod.startTime, startOrPeriod.endTime);
}
function isDateNotBetween(date, startOrPeriod, endTime) {
  const isDateBetweenSuccess = isDate(startOrPeriod) ? isNotNullOrUndefined(endTime) && isDateBetween(date, startOrPeriod, endTime) : isDateBetween(date, startOrPeriod);
  return !isDateBetweenSuccess;
}
function isDate(date) {
  return date instanceof Date;
}
function arePeriodsOverlapping(period1, period2) {
  return isDateBetween(period1.startTime, period2) || isDateBetween(period1.endTime, period2);
}
function isPeriodContainedInAnother(containedPeriod, containerPeriod) {
  return isDateBetween(containedPeriod.startTime, containerPeriod) && isDateBetween(containedPeriod.endTime, containerPeriod);
}
function arePeriodsNotOverlapping(period1, period2) {
  return !arePeriodsOverlapping(period1, period2);
}
function isDateAfterDate(date1, date2) {
  return date1.getTime() > date2.getTime();
}
function isDateBeforeDate(firstDate, secondDate) {
  return firstDate.getTime() < secondDate.getTime();
}
function isDateAfterOrEqual(date1, date2) {
  return date1.getTime() >= date2.getTime();
}
function subtractDays(date, days) {
  return vyDate(date).subtract(days, "day").toDate();
}
function utcFromString(dateStr) {
  return dateStr ? unix2(+dateStr).toDate() : void 0;
}
function now() {
  return /* @__PURE__ */ new Date();
}
function isSameMonthAndDay(date1, date2) {
  return getDateMonthAndYear(date1).split("-")[0] === getDateMonthAndYear(date2).split("-")[0] && date1.getDate() === date2.getDate();
}
function getStartOfNextDay(date) {
  return vyDate(date).add(1, "day").startOf("day").toDate();
}
function yesterday() {
  return subtractDays(now(), 1);
}
function getDaysBetween(startOrPeriod, end) {
  if (isDayjs(startOrPeriod)) {
    validateNotNullOrUndefined(end);
    return end.diff(startOrPeriod, "days");
  }
  return vyDate(startOrPeriod.endTime).diff(startOrPeriod.startTime, "days");
}
function getDaysFractionBetween(startOrPeriod, end) {
  if (isDayjs(startOrPeriod)) {
    validateNotNullOrUndefined(end);
    return end.diff(startOrPeriod, "days", true);
  }
  return startOrPeriod.endTime.diff(startOrPeriod.startTime, "days", true);
}
function getTomorrow(date = vyDate()) {
  return date.add(1, "day");
}
function buildPeriod(startTime, endTime) {
  return {
    startTime,
    endTime
  };
}
function removeDateHours(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
function getAbbreviatedMonths() {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const monthAbbreviation = vyDate().month(i).format("MMM");
    months.push(monthAbbreviation);
  }
  return months;
}
function getPeriodDays(period) {
  const daysBetween = getDaysBetween(vyDate(period.startTime), vyDate(period.endTime));
  return Array.from({ length: daysBetween + 1 }).map((_2, index) => {
    return vyDate(period.startTime).add(index, "day");
  });
}
function getPeriodsBetween(startTime, endTime) {
  const daysBetween = getDaysBetween(vyDate(startTime), vyDate(endTime));
  return Array.from({ length: daysBetween + 1 }).map((_2, index) => {
    const date = vyDate(startTime).add(index, "day");
    return {
      startTime: date.toDate(),
      endTime: date.endOf("day").toDate()
    };
  });
}
function getPeriodMonths(period) {
  const totalMonths = vyDate(period.endTime).diff(vyDate(period.startTime), "month");
  return Array.from({ length: totalMonths + 1 }).map((_2, index) => {
    return vyDate(period.startTime).add(index, "month");
  });
}
function validateDate(date) {
  if (!date.isValid()) {
    raise("Invalid date");
  }
}
function getOneMonthBefore(date) {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() - 1);
  return newDate;
}
function buildVyDatePeriod(startTime, endTime) {
  return {
    startTime: vyDate(startTime),
    endTime: vyDate(endTime)
  };
}
function mapVyDatePeriodToPeriod({ startTime, endTime }) {
  return {
    startTime: startTime.toDate(),
    endTime: endTime.toDate()
  };
}
function mapPeriodToVyDatePeriod({ startTime, endTime }) {
  return {
    startTime: vyDate(startTime),
    endTime: vyDate(endTime)
  };
}
function minimumDate(firstDate, ...dates) {
  return dates.reduce(
    (workingMinimumDate, currentDate) => currentDate < workingMinimumDate ? currentDate : workingMinimumDate,
    firstDate
  );
}
function periodToString(period) {
  return `${period.startTime.toISOString()}/${period.endTime.toISOString()}`;
}
function periodFromString(periodString) {
  const [startTime, endTime] = periodString.split("/").map((date) => new Date(date));
  return {
    startTime,
    endTime
  };
}
function getEndOfPreviousDay(date) {
  return vyDate(date).subtract(1, "day").endOf("day").toDate();
}
function convertSecondsToMilliseconds(seconds) {
  return seconds * 1e3;
}
function getYearPeriod() {
  const startOfYear = vyDate().startOf("year").toDate();
  const endOfYear = vyDate().endOf("year").toDate();
  return buildPeriod(startOfYear, endOfYear);
}
function getMonthPeriod() {
  const startOfMonth = vyDate().startOf("month").toDate();
  const endOfMonth = vyDate().endOf("month").toDate();
  return buildPeriod(startOfMonth, endOfMonth);
}
function getYtdPeriod() {
  const startOfYear = vyDate().startOf("year").toDate();
  const endOfDay = vyDate().endOf("day").toDate();
  return buildPeriod(startOfYear, endOfDay);
}
function isInThePast(date) {
  return date < now();
}
function isInTheFuture(date) {
  return date > now();
}
function getFutureDate(date, years = 5) {
  return vyDate(date).add(years, "year").toDate();
}
function extractDatesFromPeriod(startTimestamp, endTimestamp) {
  const startDate = vyDate(startTimestamp);
  const endDate = vyDate(endTimestamp);
  return {
    startDate: {
      day: startDate.date(),
      month: startDate.month(),
      year: startDate.year()
    },
    endDate: {
      day: endDate.date(),
      month: endDate.month(),
      year: endDate.year()
    }
  };
}
function convertTimestampToDate(timestamp) {
  return new Date(timestamp * 1e3);
}
function isValidStartTime(startTime) {
  const date = new Date(startTime);
  return date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
}
function isValidEndTime(endTime) {
  const date = new Date(endTime);
  return date.getUTCHours() === 23 && date.getUTCMinutes() === 59 && date.getUTCSeconds() === 59;
}
function isValidFullDayPeriod(data) {
  return isValidStartTime(data.startTime) && isValidEndTime(data.endTime);
}
function findMaxPeriodSpan(periods) {
  return periods.reduce((maxPeriod, currentPeriod) => {
    const maxEnd = maxPeriod.endTime.getTime() > currentPeriod.endTime.getTime() ? maxPeriod.endTime : currentPeriod.endTime;
    const minStart = maxPeriod.startTime.getTime() < currentPeriod.startTime.getTime() ? maxPeriod.startTime : currentPeriod.startTime;
    return buildPeriod(minStart, maxEnd);
  }, periods[0]);
}
function timeSlotFromDate(date) {
  return {
    day: vyDate(date).date(),
    month: vyDate(date).month(),
    year: vyDate(date).year()
  };
}
function dateFromTimeSlot({ day, month, year }) {
  return vyDate().year(year).month(month).date(day).startOf("day").toDate();
}
function getDateMonthAndYear(date) {
  return `${vyDate(date).month()}-${vyDate(date).year()}`;
}
function isSamePeriods(period, ...otherPeriods) {
  return otherPeriods.every((otherPeriod) => {
    return vyDate(period.startTime).isSame(otherPeriod.startTime) && vyDate(period.endTime).isSame(otherPeriod.endTime);
  });
}
function isToday(date) {
  return vyDate().isSame(date, "day");
}
function getMinDate(...dates) {
  return min(dates) ?? first2(dates);
}
function getMaxDate(...dates) {
  return max(dates) ?? first2(dates);
}
function getLongestPeriod(periods) {
  const vyDatePeriods = periods.map(mapPeriodToVyDatePeriod);
  validateNotEmptyArray(vyDatePeriods);
  return maxBy(vyDatePeriods, (period) => getDaysBetween(period)) ?? first2(vyDatePeriods);
}
function formatPeriod(period) {
  return `${vyDate(period.startTime).format("DD.MM.YYYY")} - ${vyDate(period.endTime).format("DD.MM.YYYY")}`;
}

// src/date/date-consts.ts
var ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1e3;

// src/db-object-id-builder/db-object-id-builder.ts
function buildDbObjectId(id) {
  return { $oid: id };
}

// src/env-var/env-var.ts
import { snakeCase } from "lodash";
import { VyPropertyNotFoundError } from "@vy/error";
function getEnvVar(name) {
  return process.env[name];
}
function getRequiredEnvVar(name) {
  const envVarValue = getEnvVar(name);
  if (!envVarValue) {
    throw new VyPropertyNotFoundError(`Environment variable ${name.toString()} is required`);
  }
  return envVarValue;
}
function getEnvVarOrDefault(name, defaultValue) {
  return getEnvVar(name) ?? defaultValue;
}
function setEnvVars(envVars) {
  Object.entries(envVars).forEach(([key, value]) => {
    const snakeCaseKey = snakeCase(key).toUpperCase();
    const strValue = isString(value) ? value : vySerialize(value);
    process.env[snakeCaseKey] = strValue;
  });
}

// src/id.ts
import { ObjectId } from "bson";
import { v4 as uuidv4 } from "uuid";
function newId() {
  return new ObjectId().toHexString();
}
function newUUID() {
  return uuidv4();
}

// src/json.ts
import { JsonInvalidError } from "@vy/error";
function parseJson(json) {
  validateNotEmptyString(json);
  try {
    return JSON.parse(json);
  } catch (error) {
    throw new JsonInvalidError(json);
  }
}
function isJsonParsable(json) {
  try {
    JSON.parse(json);
    return true;
  } catch (error) {
    return false;
  }
}
function parseJsonSafely(json) {
  try {
    if (isString(json)) {
      return JSON.parse(json);
    }
    return json;
  } catch (error) {
    console.warn("Failed to deserialize JSON. returning empty object", {
      json
    });
    return void 0;
  }
}

// src/map/index.ts
function groupBy(items, keyOrAccessor) {
  return items.reduce((map, item) => {
    const mapKey = isFunction(keyOrAccessor) ? keyOrAccessor(item) : item[keyOrAccessor];
    const existingItems = getMapValueOrDefault(map, mapKey, []);
    return map.set(mapKey, [...existingItems, item]);
  }, /* @__PURE__ */ new Map());
}
function maxBy2(items, keyOrAccessor) {
  if (isEmptyArray(items)) {
    return void 0;
  }
  return items.reduce((maxItem, currentItem) => {
    const current = isFunction(keyOrAccessor) ? keyOrAccessor(currentItem) : currentItem[keyOrAccessor];
    const max2 = isFunction(keyOrAccessor) ? keyOrAccessor(maxItem) : maxItem[keyOrAccessor];
    return current > max2 ? currentItem : maxItem;
  });
}
function mapBy(items, keyOrAccessor) {
  return new Map(
    items.map((item) => [isFunction(keyOrAccessor) ? keyOrAccessor(item) : item[keyOrAccessor], item])
  );
}
function arrayToCountMap(array) {
  return array.reduce(
    (countMap, currentValue) => countMap.has(currentValue) ? countMap.set(currentValue, getRequiredMapValue(countMap, currentValue) + 1) : countMap.set(currentValue, 1),
    /* @__PURE__ */ new Map()
  );
}
function getMapValue(map, key) {
  return isNullOrUndefined(key) ? void 0 : map.get(key);
}
function getRequiredMapValue(map, key) {
  const value = map.get(key);
  if (isNullOrUndefined(value)) {
    throw new Error(`No value found for key ${key}`);
  }
  return value;
}
function getMapValueOrDefault(map, key, defaultValue) {
  const value = map.get(key);
  return isNotNullOrUndefined(value) ? value : defaultValue;
}
function getMapValues(map) {
  return [...map.values()];
}
function getMapKeys(map) {
  return [...map.keys()];
}
function getFlatMapValues(map) {
  return getMapValues(map).flat();
}
function getMapEntries(map) {
  return [...map.entries()];
}
function isEmptyMap(map) {
  return map.size === 0;
}
function mapToRecord(map) {
  return Object.fromEntries(map.entries());
}
function iterateOverEntries(map, actionOnEntries) {
  return getMapEntries(map).map(actionOnEntries);
}
function iterateOverEntriesFlat(map, actionOnEntries) {
  return getMapEntries(map).flatMap(actionOnEntries);
}

// src/mongo/mongo.ts
function buildMongoIdObjects(ids) {
  return ids.map(buildMongoIdObject);
}
function buildMongoIdObject(id) {
  return { $oid: id };
}
function buildMongoDate(date) {
  const $date = date.toISOString();
  return { $date };
}

// src/openapi-zod.ts
import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
import { z } from "zod";
import { ZodError } from "zod";
extendZodWithOpenApi(z);

// src/mongo/mongo-types/mongo-date-object.ts
var MongoDateObjectSchema = z.object({
  $date: z.string().datetime()
});

// src/mongo/mongo-types/mongo-id-object.ts
var MongoObjectIdSchema = z.object({
  $oid: z.string()
});

// src/null.ts
function fromNullToUndefined(value) {
  return value ?? void 0;
}
function fromUndefinedToNull(value) {
  return value ?? null;
}
function deepFromNullToUndefined(object) {
  return Object.fromEntries(Object.entries(object).map(([key, value]) => [key, fromNullToUndefined(value)]));
}
function nullsToUndefined(obj) {
  if (obj === null || obj === void 0) {
    return void 0;
  }
  if (obj.constructor.name === "Object" || Array.isArray(obj)) {
    for (const key in obj) {
      obj[key] = nullsToUndefined(obj[key]);
    }
  }
  return obj;
}
function whenDefined(operationOnValue, value) {
  return value === void 0 ? void 0 : operationOnValue(value);
}

// src/promise/await-all-safely.ts
async function awaitAllSafely(...promises) {
  try {
    return await Promise.all(promises);
  } catch (error) {
    await Promise.allSettled(promises);
    throw error;
  }
}

// src/promise/validate-fulfilled.ts
function isFulfilled(promise) {
  return promise.status === "fulfilled";
}
function validateFulfilled(promise) {
  if (!isFulfilled(promise)) {
    raise(`Promise not fulfilled ${promise.reason}`);
  }
}

// src/promise/callback-to-promise.ts
function callbackToPromise(callback) {
  return new Promise(callback);
}

// src/promise/await-all-sequentially.ts
import { VyRuntimeError } from "@vy/error";
var RUN_SEQUENTIALLY_OPTIONS = Object.freeze({
  FAIL_FAST: "FAIL_FAST",
  CONTINUE_ON_ERROR: "CONTINUE_ON_ERROR"
});
async function awaitAllSequentially(raisingOption, ...promises) {
  const results = [];
  const errors = [];
  for (const promise of promises) {
    const { error, result } = await caughtAsync(promise, Error);
    if (error && raisingOption === RUN_SEQUENTIALLY_OPTIONS.FAIL_FAST) {
      raise(error);
    }
    errors.push(error);
    results.push(result);
  }
  const purgedErrors = purge(errors);
  if (purgedErrors.length > 0) {
    const vyError = buildRuntimeError(errors);
    raise(vyError);
  }
  return results;
}
function buildRuntimeError(errors) {
  return new VyRuntimeError(`awaitAllSequentially failed with ${errors.length} errors`, void 0, {
    errors
  });
}

// src/random.ts
function random(min2, max2) {
  return Math.random() * (max2 - min2) + min2;
}

// src/sleep.ts
async function sleep(ms, options = {}) {
  const { shouldLog = true } = options;
  const sleeping = new Promise((resolve) => setTimeout(resolve, ms));
  await sleeping;
  if (shouldLog) {
    console.info("waking up from sleep", { ms });
  }
}

// src/retry/retry-types/backoff-strategy.ts
var BACKOFF_STRATEGIES = {
  FIXED: "fixed",
  LINEAR: "linear",
  EXPONENTIAL: "exponential"
};

// src/retry/index.ts
async function retry(fu, options) {
  let attempt = 0;
  let failedAttemptResult = null;
  while (attempt < options.retries) {
    const attemptResult = await tryAttempt(fu, attempt);
    if (attemptResult.success) {
      return attemptResult.result;
    }
    failedAttemptResult = attemptResult;
    await sleepBetweenAttempts(attemptResult, options);
    attempt++;
  }
  validateNotNullOrUndefined(failedAttemptResult, "retryAttemptResult");
  throw failedAttemptResult.error;
}
async function tryAttempt(fu, attemptsSoFar) {
  const attempt = attemptsSoFar + 1;
  try {
    const result = await fu();
    return {
      success: true,
      attempt,
      result
    };
  } catch (error) {
    return {
      success: false,
      attempt,
      error
    };
  }
}
async function sleepBetweenAttempts(attemptResult, options) {
  const delayInMilliseconds = calculateNewDelay(options);
  console.warn(
    `Attempt ${attemptResult.attempt} failed, trying again in ${delayInMilliseconds} milliseconds`,
    attemptResult.error
  );
  await sleep(delayInMilliseconds);
}
function calculateNewDelay(retryOptions) {
  const delay = getDelayInMilliseconds(retryOptions);
  return retryOptions.jitter ? applyJitter(delay, retryOptions.jitter) : delay;
}
function getDelayInMilliseconds(retryOptions) {
  switch (retryOptions.strategy) {
    case BACKOFF_STRATEGIES.FIXED:
      return retryOptions.delay;
    case BACKOFF_STRATEGIES.LINEAR:
      return retryOptions.delay + retryOptions.growthInMillSeconds;
    case BACKOFF_STRATEGIES.EXPONENTIAL:
      return retryOptions.delay * retryOptions.growthInMillSeconds;
    default:
      return raiseUnreachable(retryOptions);
  }
}
function applyJitter(delay, jitterRangePercentage) {
  const min2 = delay * (1 - jitterRangePercentage);
  const max2 = delay * (1 + jitterRangePercentage);
  return random(min2, max2);
}

// src/stream.ts
async function streamToBuffer(stream) {
  const chunks = [];
  return new Promise((resolve, reject) => {
    stream.on("data", (chunk) => {
      const bufferChunk = Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk);
      chunks.push(bufferChunk);
    });
    stream.on("end", () => resolve(Buffer.concat(chunks)));
    stream.on("error", reject);
  });
}
export {
  ApiError,
  ApiValidationError,
  BACKOFF_STRATEGIES,
  BadCredentialsError,
  CASE_INSENSITIVE_REGEX,
  EMPTY_ARRAY,
  EMPTY_BATCH_ITERATION_DATA,
  EMPTY_OBJECT,
  EMPTY_STRING,
  MongoDateObjectSchema,
  MongoObjectIdSchema,
  NEW_LINE,
  ONE_DAY_IN_MILLISECONDS,
  PathParamError,
  QueryParamError,
  RUN_SEQUENTIALLY_OPTIONS,
  RequestPayloadError,
  ResourceNotFoundError,
  ResourceNotFoundSparseError,
  SPACE,
  UNKNOWN_PARAM,
  VALIDATION_ERROR_CODES,
  VyEmptyObjectError,
  VyError,
  VyNullOrUndefinedError,
  ZodError,
  append,
  areAllNullOrUndefined,
  arePeriodsNotOverlapping,
  arePeriodsOverlapping,
  areSomeNullOrUndefined,
  arrayToCountMap,
  asLastOrEmptyArray,
  avgBy,
  awaitAllSafely,
  awaitAllSequentially,
  batch,
  batchByMaxPayloadSize,
  buildDbObjectId,
  buildMongoDate,
  buildMongoIdObject,
  buildMongoIdObjects,
  buildPeriod,
  buildVyDatePeriod,
  calculatePercentageFromNumber,
  callbackToPromise,
  caseInsensitiveIncludes,
  caught,
  caughtAsync,
  clonedArray,
  conditionalWithDefault,
  convertSecondsToMilliseconds,
  convertTimestampToDate,
  dateFromTimeSlot,
  debounce,
  deepFromNullToUndefined,
  desanitizeFilename,
  duration2 as duration,
  entries,
  escapeRegex,
  every,
  extend,
  extendInIndex,
  extract,
  extractDatesFromPeriod,
  extractIds,
  extractValues,
  fillArrayOfLength,
  filter,
  filterBy,
  filterByExact,
  findMaxPeriodSpan,
  findRequired,
  findRequiredIndex,
  findRequiredIndexOf,
  first,
  flatExtract,
  formatNumber,
  formatPeriod,
  formatZodError,
  fromNullToUndefined,
  fromUndefinedToNull,
  getAbbreviatedMonths,
  getDateMonthAndYear,
  getDaysBetween,
  getDaysFractionBetween,
  getEndOfPreviousDay,
  getEnvVar,
  getEnvVarOrDefault,
  getFlatMapValues,
  getFlatObjectValues,
  getFutureDate,
  getLongestPeriod,
  getMapEntries,
  getMapKeys,
  getMapValue,
  getMapValueOrDefault,
  getMapValues,
  getMaxDate,
  getMinDate,
  getModifiedFields,
  getMonthPeriod,
  getObjectLength,
  getObjectPropertyOrDefault,
  getOneMonthBefore,
  getOptionalObjectProperty,
  getPeriodDays,
  getPeriodMonths,
  getPeriodsBetween,
  getRequiredEnvVar,
  getRequiredMapValue,
  getRequiredObjectProperty,
  getStartOfNextDay,
  getTomorrow,
  getValuesBetweenIncluding,
  getYearPeriod,
  getYtdPeriod,
  groupBy,
  handleUnknownError,
  intersect,
  isAccessor,
  isArray,
  isBetweenExclusive,
  isBetweenInclusive,
  isBiggerThan,
  isDate,
  isDateAfterDate,
  isDateAfterOrEqual,
  isDateBeforeDate,
  isDateBetween,
  isDateNotBetween,
  isDeepEmptyObject,
  isEmptyArray,
  isEmptyMap,
  isEmptyObject,
  isEmptyString,
  isError,
  isErrorWithFormattedZodError,
  isFulfilled,
  isFunction,
  isInTheFuture,
  isInTheMillions,
  isInThePast,
  isJsonParsable,
  isKeyOf,
  isLengthGreaterThan,
  isLengthOf,
  isMultiple,
  isNegative,
  isNonNegative,
  isNotArray,
  isNotBetweenInclusive,
  isNotEmptyArray,
  isNotEmptyObject,
  isNotEmptyString,
  isNotIn,
  isNotNullOrUndefined,
  isNotNumber,
  isNotString,
  isNullOrUndefined,
  isNumber,
  isNumberValid,
  isObject,
  isPeriodContainedInAnother,
  isPositive,
  isSameMonthAndDay,
  isSamePeriods,
  isSingleItem,
  isString,
  isToday,
  isValidEndTime,
  isValidFullDayPeriod,
  isValidStartTime,
  iterateOverEntries,
  iterateOverEntriesFlat,
  jsonStringToList,
  keyExists,
  keys,
  last,
  makeMultiple,
  mapBy,
  mapPeriodToVyDatePeriod,
  mapToRecord,
  mapVyDatePeriodToPeriod,
  maxBy2 as maxBy,
  minimumDate,
  newArrayOfLength,
  newId,
  newUUID,
  now,
  nullsToUndefined,
  omit,
  omitKey,
  outersect,
  outersectBy,
  z as oz,
  parseJson,
  parseJsonSafely,
  parseNumber,
  parseNumberOrDefault,
  percentage,
  percentageRatio,
  periodFromString,
  periodToString,
  purge,
  purgeFlat,
  purgeMap,
  raise,
  raiseUnreachable,
  raiseUnsupported,
  random,
  remove,
  removeBy,
  removeByField,
  removeDateHours,
  removeInIndex,
  removeUndefinedAndNullValues,
  replaceInIndex,
  requiredAt,
  requiredLast,
  retry,
  runSafely,
  safeDivision,
  sanitizeFilename,
  serializeToJsonBuffer,
  setEnvVars,
  shortenString,
  sleep,
  sort,
  sortDescending,
  streamToBuffer,
  substringFromEnd,
  subtractDays,
  sum,
  sumBy,
  take,
  takeFromEnd,
  takeFromStart,
  timeSlotFromDate,
  toLowerCase,
  toNegativeOptional,
  toUpperCase,
  uint8ArrayToBase64,
  uniq,
  uniqueArray,
  uniqueBy,
  uniqueExtract,
  uniqueObjects,
  unix,
  utcFromString,
  validateArray,
  validateDate,
  validateEnum,
  validateEnumArray,
  validateError,
  validateFulfilled,
  validateIsArray,
  validateIsSingleItem,
  validateIsString,
  validateIsStringArray,
  validateNotEmptyArray,
  validateNotEmptyObject,
  validateNotEmptyString,
  validateNotMultipleItems,
  validateNotNullOrUndefined,
  validateNumber,
  values,
  vyDate,
  vySerialize,
  whenDefined,
  whenNotEmptyArray,
  withDefault,
  withNewOptionalValue,
  withNumberToPrecision,
  yesterday
};
